import type { ApiResponse } from '@shared/types'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useFlowStore } from '../store'
import type { FlowData } from '../types'

export type RenameStatus = 'idle' | 'saving' | 'saved' | 'error'

interface UseRenameFlowOptions {
  // The in-builder title editor renames the currently open flow, so it also keeps the
  // store name in sync optimistically. The list dialog renames an arbitrary row and must
  // NOT touch the builder's store name.
  updateStoreName?: boolean
  onSuccess?: (name: string) => void
  onError?: (message: string) => void
}

interface RenameContext {
  previousFlowData?: FlowData
  previousStoreName: string | null
}

const SAVED_INDICATOR_MS = 2000

/**
 * Write a flow's name into the flow-data query cache. Shared by the rename mutation's
 * optimistic update and the Vibe Agent's name suggestion so the cache shape lives in one
 * place. No-ops when the id is empty or the flow isn't cached yet (an unsaved flow).
 */
export const writeFlowNameToCache = (
  queryClient: QueryClient,
  flowId: string | undefined,
  name: string,
) => {
  if (!flowId) return
  queryClient.setQueryData<FlowData>(flowQueryKeys().flowData(flowId), (old) =>
    old ? { ...old, phlo: { ...old.phlo, name } } : old,
  )
}

/**
 * Shared rename mutation for a persisted flow. Optimistically updates the flow-data cache
 * (and, opt-in, the store name), rolls back on error, and refreshes the flows list on
 * success. Exposes a small status machine for inline "saving / saved / error" feedback.
 */
export const useRenameFlow = (flowId: string, options: UseRenameFlowOptions = {}) => {
  const { updateStoreName = false, onSuccess, onError } = options
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<RenameStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const mutation = useMutation<
    unknown,
    AxiosError<ApiResponse<{ message: string }>>,
    string,
    RenameContext
  >({
    mutationFn: (name) => settingsService.updateFlow(flowId, { name }),
    onMutate: (name) => {
      if (savedTimer.current) clearTimeout(savedTimer.current)
      setStatus('saving')
      setErrorMessage(null)

      const queryKey = flowQueryKeys().flowData(flowId)
      const previousFlowData = queryClient.getQueryData<FlowData>(queryKey)
      const previousStoreName = useFlowStore.getState().currentFlowName

      writeFlowNameToCache(queryClient, flowId, name)
      if (updateStoreName) useFlowStore.getState().setCurrentFlowName(name)

      return { previousFlowData, previousStoreName }
    },
    onError: (error, _name, context) => {
      queryClient.setQueryData(flowQueryKeys().flowData(flowId), context?.previousFlowData)
      if (updateStoreName) {
        useFlowStore.getState().setCurrentFlowName(context?.previousStoreName ?? null)
      }

      const message = getErrorText(error) || 'Failed to rename agent.'
      setStatus('error')
      setErrorMessage(message)
      onError?.(message)
    },
    onSuccess: (_data, name) => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
      setStatus('saved')
      savedTimer.current = setTimeout(() => setStatus('idle'), SAVED_INDICATOR_MS)
      onSuccess?.(name)
    },
  })

  const reset = useCallback(() => {
    if (savedTimer.current) clearTimeout(savedTimer.current)
    setStatus('idle')
    setErrorMessage(null)
  }, [])

  useEffect(() => () => clearTimeout(savedTimer.current ?? undefined), [])

  return {
    rename: mutation.mutate,
    isPending: mutation.isPending,
    status,
    errorMessage,
    reset,
  }
}
